import { Subject } from 'rxjs/internal/Subject';

export class CacheService {
  constructor() {
    this.cache = new Map();
  }

  private cache: Map<string, { value: any; timerId: NodeJS.Timeout | undefined; expiresOn: Date }>;

  set(key: string, value: any, ttl: number, deleteAfterExpiry?: boolean): void {
    this.delete(key); // delete any existing item with the same key

    const timerId = deleteAfterExpiry
      ? setTimeout(() => {
        this.delete(key);
      }, ttl)
      : undefined;

    this.cache.set(key, { value, timerId, expiresOn: new Date(Date.now() + ttl) });
  }

  get(key: string, ignoreExpiry?: boolean): any {
    const cachedItem = this.cache.get(key);

    if (!cachedItem || cachedItem.expiresOn < new Date() && !ignoreExpiry) {
      return undefined;
    }

    return cachedItem.value;
  }

  getOrSet<T>(key: string, factory: () => T, ttl: number, ignoreExpiry?: boolean): T {
    let value = this.get(key, ignoreExpiry) as T | undefined;

    if (value === undefined) {
      value = factory();
      this.set(key, value, ttl, ignoreExpiry);
      if (value instanceof Promise) {
        value.catch(() => this.delete(key));
      }
    }

    return value;
  }

  delete(key: string): void {
    const cachedItem = this.cache.get(key);
    if (cachedItem && cachedItem.timerId) {
      clearTimeout(cachedItem.timerId);
    }

    this.cache.delete(key);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }
}
